/**
 * Stella Polaris — minimal i18n.
 * Single source of truth for all landing-page + apply-modal copy.
 *
 * Usage:
 *   <LangProvider lang="es"><Landing /></LangProvider>
 *   const t = useT();   // returns the dict for the active language
 *   t.hero.title
 *
 * Language is derived from the URL path:
 *   /        → en
 *   /es, /es/...   → es
 */
import { createContext, useContext } from "react";

export const LANGS = ["en", "es"];
export const DEFAULT_LANG = "en";

export const langFromPath = (pathname = "/") => {
  const seg = pathname.split("/").filter(Boolean)[0];
  return LANGS.includes(seg) ? seg : DEFAULT_LANG;
};

export const pathForLang = (lang, pathname = "/") => {
  const parts = pathname.split("/").filter(Boolean);
  if (LANGS.includes(parts[0])) parts.shift();
  const rest = parts.join("/");
  if (lang === DEFAULT_LANG) return "/" + (rest ? rest : "");
  return `/${lang}${rest ? "/" + rest : ""}`;
};

const LangCtx = createContext(DEFAULT_LANG);

export const LangProvider = ({ lang, children }) => (
  <LangCtx.Provider value={lang || DEFAULT_LANG}>{children}</LangCtx.Provider>
);

export const useLang = () => useContext(LangCtx);
export const useT = () => COPY[useContext(LangCtx)] || COPY[DEFAULT_LANG];

export const COPY = {
  en: {
    htmlLang: "en",
    nav: {
      brand: "Stella Polaris",
      tag: "Executive & Leadership Coaching",
      links: [
        { href: "#problem", label: "Constraint" },
        { href: "#approach", label: "Approach" },
        { href: "#program", label: "Program" },
        { href: "#results", label: "Results" },
        { href: "#process", label: "Process" },
      ],
      insights: "Insights",
      about: "About",
      contact: "Contact",
      apply: "Apply",
      langSwitch: { en: "EN", es: "ES" },
    },
    hero: {
      eyebrow: "Executive Performance Coaching · By Application Only",
      titlePre: "Your next level of output is not a ",
      titleEm: "strategy",
      titlePost: " problem.",
      sub: "The executives and founders who break through ceilings don't work harder. They operate differently — at a biological, psychological, and behavioral level. Gaia Ferreira works with a select group of high-stakes leaders to engineer exactly that shift.",
      ctaPrimary: "See If You Qualify",
      ctaSecondary: "How It Works",
      stats: [
        { num: "24,000+", label: "Hours of executive coaching delivered" },
        { num: "4", label: "Languages · International client base" },
        { num: ">60%", label: "Avg. revenue growth reported in first 6 months" },
        { num: "12-mo", label: "Engagement · Structured & measurable" },
      ],
    },
    problem: {
      eyebrow: "The Real Constraint",
      titlePre: "High performers plateau — not from lack of ambition,",
      titlePost1: "but from ",
      titleEm: "unexamined operating systems",
      titlePost2: ".",
      lead: "The patterns that made you successful at one level are often the same ones creating friction at the next. Most executives never identify them — because they're invisible from the inside.",
      items: [
        {
          n: "01",
          title: "Decision fatigue at scale",
          body: "As the stakes grow, so does the cognitive load of every call you make. Speed and quality start to degrade — not from lack of information, but from how the brain processes high-pressure uncertainty.",
        },
        {
          n: "02",
          title: "Leadership presence under pressure",
          body: "The ability to remain clear, composed, and authoritative when markets move, teams fracture, or deals stall — this isn't personality. It's a trainable, measurable skill. Most leaders haven't trained it.",
        },
        {
          n: "03",
          title: "Output without recovery",
          body: "Chronic high output without structured cognitive recovery doesn't just cause burnout — it reduces the quality of judgment before burnout ever arrives. The body's impact on performance is not optional to understand.",
        },
        {
          n: "04",
          title: "Execution without ownership",
          body: "Many founders and senior executives are exceptional operators. Fewer are originators — leaders who set direction from conviction rather than reaction. The shift from one to the other is the defining move at the top.",
        },
      ],
    },
    differentiator: {
      eyebrow: "The Approach",
      title: "Precision performance coaching — not personal development.",
      lead: "This is not a general coaching program. It is a structured, data-informed, individually designed engagement built around one variable: the gap between where you currently operate and your actual ceiling.",
      pillars: [
        {
          title: "Biological baseline first",
          body: "Every engagement begins with a medical-grade assessment of how your body actually processes stress, regulates energy, and recovers. Coaching built on this foundation accelerates results by working with your specific physiology — not a generic framework.",
        },
        {
          title: "Behavioral pattern identification",
          body: "Limiting patterns at the executive level are rarely obvious. They're embedded in communication habits, decision heuristics, and leadership defaults. Identifying and redesigning them requires a diagnostic approach, not generic goal-setting.",
        },
        {
          title: "Measurable performance outcomes",
          body: "Every element of the engagement is mapped to observable, trackable performance improvements — decision quality, leadership impact, cognitive output, team alignment. Progress is not abstract.",
        },
      ],
      quotes: [
        {
          q: "Working with Gaia added a level of clarity and consistency that I did not think was possible. The experience is truly transformational in all areas of my life.",
          c: "Matas J. · CEO & Co-Founder, ForceField",
        },
        {
          q: "After just the first 3 months of working with Gaia, I've gained clarity and I'm in one of the most productive phases of my professional life.",
          c: "John L. · CEO & Founder, SpartanBrands",
        },
        {
          q: "I started with business in mind but today it is about life and all the dimensions of leading. Coaching with Gaia has been a revolution for me.",
          c: "Birgir · Serial Entrepreneur",
        },
      ],
    },
    deliverables: {
      eyebrow: "Program Structure",
      titlePre: "12 months. Fully structured. ",
      titleEm: "Built around you.",
      lead: "Every deliverable is mapped to a measurable performance outcome. Nothing in this engagement exists for optics. Everything exists to move the needle on how you operate.",
      items: [
        { n: "01", title: "Performance Baseline Assessment", body: "Medical-grade genetic and biomarker profiling to establish how your physiology influences cognition, stress response, energy management, and decision quality. This informs every intervention that follows." },
        { n: "02", title: "Weekly 1:1 Coaching Sessions", body: "Private, structured sessions with a singular focus: closing the gap between your current operating level and your capacity ceiling. Sessions are adaptive — following your live business context, not a fixed curriculum." },
        { n: "03", title: "Decision Architecture Review", body: "Systematic analysis of how you make high-stakes decisions — where speed, bias, or cognitive load degrades quality. Followed by a redesign of your personal decision framework for greater speed and precision." },
        { n: "04", title: "Leadership Presence Development", body: "Communication precision, influence without friction, and composure under pressure. Measured by how your team responds to you — not by abstract leadership scores." },
        { n: "05", title: "Physiological Performance Protocol", body: "Personalized nutrition, sleep architecture, and recovery protocols based on your genetic profile. Cognitive stamina is a function of physical inputs. This is not lifestyle advice — it is performance engineering." },
        { n: "06", title: "Quarterly Performance Reviews", body: "Structured quarterly reviews against measurable outcomes. Benchmarks are set at the start and tracked throughout. You will know exactly what has changed and by how much — at every stage of the engagement." },
      ],
    },
    socialProof: {
      eyebrow: "Client Outcomes",
      titlePre: "Results that show up where it counts — ",
      titleEm: "in the business.",
      quote1: "\u201CNot only has my business grown over 41% in the last 6 months — I have seen my physical fitness, my relationships, and my mental output all improve. I now have complete clarity that everything is interconnected and that I have full agency to build and perform at the level I choose.\u201D",
      attr1: "Michael · Real Estate Developer",
      metric1Num: ">60%",
      metric1Label: "Average revenue increase reported by clients within the first 6 months of engagement",
      metric2Num: "4yr+",
      metric2Label: "Average client engagement duration — most clients continue well beyond the initial 12 months",
      quote2: "\u201CGaia helped me identify behavioral patterns I had never named — and couldn't have named without her diagnostic approach. As a coach myself, it takes a great deal to impress me and produce results I can't work through independently. To say I was impressed is an understatement.\u201D",
      attr2: "Jessie B. · Executive Coach & Network Club Founder",
    },
    credentials: {
      eyebrow: "Background",
      titleLine1: "An advisor calibrated for the stakes",
      titleLine2: "you're actually operating at.",
      lead: "Gaia Ferreira is a transformation coach and behavioral strategist with over 20,000 hours of one-to-one work with high-achieving entrepreneurs, investors, executives, and founders across four continents. Trained in Psychology with advanced certifications in NLP, hypnotherapy, and nutrigenomics, she specializes in the intersection of biology, behavioral science, and peak performance, translating hard science into personalized protocols that create measurable, lasting change. She works with teams focused on sharper, more decisive, and sustainable organizations, and the leaders navigating high-stakes transitions, performance plateaus, and the human cost of building at scale.",
      items: [
        { title: "Top 1% Selection Rate", body: "Accepted into one of the world's most competitive executive coaching programs before founding an award-winning independent practice serving CEOs, founders, and investment professionals across four continents." },
        { title: "24,000+ Coaching Hours", body: "Delivered across four languages with a client base spanning CEOs, co-founders, investment professionals, and senior executives." },
        { title: "Built in the Real World", body: "Seven years as a Marketing Executive in Tech. Eight years as an entrepreneur in Hospitality, including her own TV show. Eleven years coaching high-stakes leaders. The method she uses isn't theoretical; it was tested across industries, cultures, and the full arc of building something from nothing." },
        { title: "Proprietary Methodology", body: "Every engagement is built on a personalized protocol unique to each client, designed to identify root-cause patterns and produce outcomes that standard coaching doesn't reach." },
      ],
      readMore: "Read Gaia's full story",
    },
    contrast: {
      eyebrow: "Why Not the Alternatives",
      titlePre: "Not all coaching is built for ",
      titleEm: "this",
      titlePost: " level.",
      lead: "Institutional programs, certification-mill coaches, and motivational speaking each have their place. None of them deliver what this engagement delivers.",
      capability: "Capability",
      thisEngagement: "This engagement",
      rows: [
        "Individualized to your biology",
        "Measurable performance benchmarks",
        "Exclusive 1:1 access to senior coach",
        "Behavioral pattern redesign (not just goal-setting)",
        "Physiological performance engineering",
        "Near-exclusively referral-based client selection",
      ],
      cols: [
        "Certification-based coaching",
        "Corporate programs (BetterUp, etc.)",
        "Motivational speakers",
      ],
    },
    process: {
      eyebrow: "How Engagement Begins",
      title: "A process as deliberate as the work itself.",
      lead: "Gaia works with a small number of clients at any time. Engagement begins with a structured qualification process — because the right fit determines whether results are possible at all.",
      stepLabel: "Step",
      steps: [
        { title: "Apply for a Discovery Call", body: "Submit three qualifying questions to determine whether this engagement is appropriate for your current context and ambitions." },
        { title: "Discovery Conversation", body: "A focused 45-minute call to identify your specific performance constraints, current operating context, and whether there is a clear fit for engagement." },
        { title: "Engagement Design", body: "If selected, your 12-month program is designed specifically around your role, your business, and your performance profile — not a standardized curriculum." },
        { title: "Measurable Engagement", body: "Week one begins with your biological baseline assessment. From there, every session, protocol, and review milestone is mapped to observable outcomes." },
      ],
    },
    finalCTA: {
      titleLine1: "This is not for everyone.",
      titleLine2Pre: "It may be exactly right for ",
      titleLine2Em: "you.",
      body: "If you are a founder or senior executive operating at a level where decisions have real weight — and you have reason to believe there is a meaningful gap between where you currently operate and what you're actually capable of — this conversation is worth having.",
      cta: "Apply for a Discovery Call",
      disclaimer: "Engagements are offered to a limited number of clients · Primarily by referral · All applications are reviewed personally",
    },
    team: {
      eyebrow: "Also Available",
      title: "Need something for the whole team?",
      body: "Team trainings, leadership intensives, and group programs are available — designed for leadership teams and high-performance groups who want the same precision applied at scale. Format, scope, and delivery are tailored to your team's size and objectives.",
      cta: "Book a Discovery Call",
    },
    footer: {
      tagline: "Executive & Leadership Coaching",
      bottomLeft: (year) => `\u00A9 ${year} Stella Polaris \u00B7 Executive & Leadership Coaching by Gaia Ferreira`,
      bottomRight: "By application only · International practice",
      apply: "Apply",
      appliedLabel: "Under review",
    },
    applyCTA: {
      applied: "Application under review",
      appliedShort: "Under review",
    },
    apply: {
      step1Eyebrow: "Step 01 · Three qualifying questions",
      step2Eyebrow: "Step 02 · Your details",
      step1TitlePre: "Apply for a ",
      step1TitleEm: "Discovery Call",
      step1TitlePost: ".",
      step2Title: "Where can Gaia reach you?",
      step1Sub: "Engagements are reviewed personally. These three questions help determine fit before we speak.",
      step2Sub: "Each application is read by Gaia. You will hear back within five business days.",
      labelRole: "01 · Your current role",
      labelChallenge: "02 · The constraint most relevant to you right now",
      labelReadiness: "03 · Your readiness to engage",
      labelName: "Full name",
      labelEmail: "Email",
      labelPhone: "Phone (optional)",
      phonePlaceholder: "+1 555 123 4567",
      labelCompany: "Company / Organization (optional)",
      labelReferral: "How did you hear about Stella Polaris? (optional)",
      friendDetail: "Their name (optional)",
      otherDetail: "Please specify",
      labelNotes: "Anything Gaia should know before the call (optional)",
      notesPlaceholder: "Context, current priorities, or what would make this conversation valuable for you.",
      cancel: "Cancel",
      next: "Continue",
      back: "Back",
      submit: "Submit application",
      submitting: "Submitting…",
      err1: "Please answer all three qualifying questions to continue.",
      err2: "Please add your name and a valid email.",
      errGeneric: "Something went wrong submitting your application. Please try again.",
      closeLabel: "Close application form",
      success: {
        eyebrow: "Application received",
        titlePre: "Your application has been ",
        titleEm: "received.",
        body: "Each application is read personally by Gaia. If there is a clear fit, you will receive an invitation to a 45-minute discovery conversation within five business days.",
        close: "Close",
      },
      roles: [
        { value: "founder_ceo", label: "Founder / CEO" },
        { value: "c_suite", label: "C-suite (CFO, COO, CTO, CMO…)" },
        { value: "senior_executive", label: "Senior executive / VP / Director" },
        { value: "investor", label: "Investor / partner" },
        { value: "other", label: "Other senior leader" },
      ],
      challenges: [
        { value: "decision_quality", label: "Decision quality under pressure" },
        { value: "leadership_presence", label: "Leadership presence & influence" },
        { value: "recovery_burnout", label: "Output, recovery, and energy management" },
        { value: "ownership_vision", label: "Ownership, vision, and direction-setting" },
        { value: "team_performance", label: "Team alignment & performance at scale" },
        { value: "multiple", label: "Several of the above — I'd like to discuss" },
      ],
      readiness: [
        { value: "ready_now", label: "Ready to begin within 30 days" },
        { value: "next_quarter", label: "Within the next quarter" },
        { value: "exploring", label: "Exploring — gathering information" },
      ],
      referrals: [
        { value: "social_media", label: "Social media" },
        { value: "google_ads", label: "Google ads" },
        { value: "facebook_ads", label: "Facebook ads" },
        { value: "friend", label: "A friend" },
        { value: "other", label: "Other" },
      ],
    },
    insights: {
      navLabel: "Insights",
      indexEyebrow: "Insights",
      indexTitle: "Notes from the field.",
      indexLead: "Working notes on executive performance, leadership under pressure, and the operating systems behind the world's most consequential decisions.",
      readMore: "Read",
      comingSoon: "Coming soon",
      backToHome: "← Back to home",
      backToIndex: "← All insights",
      publishedLabel: "Published",
    },
    about: {
      eyebrow: "About",
      title: "An advisor calibrated for the stakes you're actually operating at.",
      intro: "Gaia Ferreira is a transformation coach and behavioral strategist with over 24,000 hours of one-to-one work with high-achieving entrepreneurs, investors, executives, and founders across four continents. Trained in Psychology with advanced certifications in NLP, hypnotherapy, and nutrigenomics, she specializes in the intersection of biology, behavioral science, and peak performance — translating hard science into personalized protocols that create measurable, lasting change.",
      itemsHeading: "What stands behind the work.",
      items: [
        { title: "Top 1% Selection Rate", body: "Accepted into one of the world's most competitive executive coaching programs before founding an award-winning independent practice serving CEOs, founders, and investment professionals across four continents." },
        { title: "24,000+ Coaching Hours", body: "Delivered across four languages with a client base spanning CEOs, co-founders, investment professionals, and senior executives." },
        { title: "Built in the Real World", body: "Seven years as a Marketing Executive in Tech. Eight years as an entrepreneur in Hospitality, including her own TV show. Eleven years coaching high-stakes leaders. The method she uses isn't theoretical — it was tested across industries, cultures, and the full arc of building something from nothing." },
        { title: "Proprietary Methodology", body: "Every engagement is built on a personalized protocol unique to each client, designed to identify root-cause patterns and produce outcomes that standard coaching doesn't reach." },
      ],
      storyEyebrow: "The story",
      storyTitle: "A long road from there to here.",
      storyLead: "What follows is a deeper look at the path that led to Stella Polaris — written in Gaia's own words.",
      storyParas: [
        "I didn't plan to become a coach.",
        "I spent 7 years in tech as a marketing executive, then 8 years building a series of my own hospitality businesses — including my own TV show. I was good at performing. At producing. At making things look polished from the outside while quietly running on empty inside.",
        "I knew how to build things. I just didn't know how to sustain myself while doing it.",
        "The shift came slowly, then all at once. I started noticing that the most driven people around me were struggling in the same invisible ways I was. Brilliant decision-makers who couldn't sleep. Founders who'd built something real and felt nothing. Executives who \u201Cperformed\u201D confidence in the boardroom and fell apart at home. When a friend of mine died, leaving 3 kids behind, the same as mine — I paid attention. The loss hit me hard.",
        "The conventional answer to life's bumps was therapy, or mindset work, or a better morning routine. I tried most of them. I even worked for the world's largest coaching company to get the inside scoop on how to unleash a better-performing version of myself. They helped, partially. But they treated the symptoms without ever getting to the wiring underneath.",
        "So I went looking for what actually worked. I'd already studied psychology formally. I added NLP. Then hypnotherapy. Then nutrition with a specialization in nutrigenomics, because I kept watching people do the mental work and still feel stuck in their bodies, their energy, their biology. I needed to understand why.",
        "What I found was that high performance doesn't break down in one place. It breaks down across body, mind, and energy all at once: how you're wired biologically, what your nervous system learned to do under pressure, and the decisions you made about yourself so long ago you've stopped questioning them.",
        "Stella Polaris exists because I needed it to exist. And because the leaders I work with needed something that could hold all of that, without oversimplifying it into a framework that fits everyone and truly helps no one (not permanently, anyway).",
        "I work with a deliberately small number of people each year because my work is deep, with personalized protocols and a high-touch experience. That's why the results don't reverse when the engagement ends.",
        "We're all doing life for the very first time. Working together improves the quality of it with less effort.",
      ],
      ctaTitle: "Think you might be a fit?",
      ctaSub: "Engagement begins with a structured application. Each one is read personally.",
      cta: "Apply for a Discovery Call",
    },
    contact: {
      eyebrow: "Contact",
      title: "Two ways to start a conversation.",
      lead: "Stella Polaris keeps a deliberately small client base, and Gaia responds personally to every relevant inquiry. Choose the path that matches what you're after.",
      sections: {
        apply: {
          eyebrow: "01 · Want to work with Gaia?",
          title: "Apply for a discovery call.",
          body: "If you're a founder or senior executive considering a 12-month engagement, the application is the right first step. Three qualifying questions, then your details. Reviewed personally within five business days.",
          cta: "Apply for a Discovery Call",
        },
        inquiry: {
          eyebrow: "02 · Looking for a trainer, speaker, or podcast guest?",
          title: "Send a general inquiry.",
          body: "For media, partnerships, team trainings, leadership intensives, or to invite Gaia onto your stage or podcast — use the form below.",
        },
      },
      form: {
        labelName: "Full name",
        labelCompany: "Company / Organization",
        labelEmail: "Email",
        labelPhone: "Phone",
        phonePlaceholder: "+1 555 123 4567",
        labelMessage: "I'm specifically interested in connecting with you because…",
        messagePlaceholder: "A few sentences on context, timing, audience, or whatever helps Gaia decide whether to engage.",
        submit: "Send inquiry",
        submitting: "Sending…",
        cancel: "Clear",
        err1: "Please add your name and a valid email.",
        err2: "Please share a few words about why you'd like to connect.",
        errGeneric: "Something went wrong sending your message. Please try again.",
      },
      success: {
        eyebrow: "Inquiry received",
        titlePre: "Your message has been ",
        titleEm: "received.",
        body: "Thank you for reaching out. Each message is read personally by Gaia. Where there is a clear fit, you will hear back within five business days.",
        sendAnother: "Send another",
      },
    },

  },
  es: {
    htmlLang: "es",
    nav: {
      brand: "Stella Polaris",
      tag: "Coaching Ejecutivo y de Liderazgo",
      links: [
        { href: "#problem", label: "Restricción" },
        { href: "#approach", label: "Enfoque" },
        { href: "#program", label: "Programa" },
        { href: "#results", label: "Resultados" },
        { href: "#process", label: "Proceso" },
      ],
      insights: "Análisis",
      about: "Sobre",
      contact: "Contacto",
      apply: "Aplicar",
      langSwitch: { en: "EN", es: "ES" },
    },
    hero: {
      eyebrow: "Coaching de Rendimiento Ejecutivo · Solo por Aplicación",
      titlePre: "Tu próximo nivel de rendimiento no es un problema de ",
      titleEm: "estrategia",
      titlePost: ".",
      sub: "Los ejecutivos y fundadores que superan techos no trabajan más duro. Operan de forma distinta — a nivel biológico, psicológico y conductual. Gaia Ferreira trabaja con un grupo selecto de líderes de alto nivel para diseñar exactamente ese cambio.",
      ctaPrimary: "Comprueba si Calificas",
      ctaSecondary: "Cómo Funciona",
      stats: [
        { num: "24.000+", label: "Horas de coaching ejecutivo impartidas" },
        { num: "4", label: "Idiomas · Cartera internacional de clientes" },
        { num: ">60%", label: "Crecimiento medio de ingresos en los primeros 6 meses" },
        { num: "12 meses", label: "Compromiso · Estructurado y medible" },
      ],
    },
    problem: {
      eyebrow: "La Verdadera Restricción",
      titlePre: "Los altos rendidores se estancan — no por falta de ambición,",
      titlePost1: "sino por ",
      titleEm: "sistemas operativos no examinados",
      titlePost2: ".",
      lead: "Los patrones que te llevaron al éxito en un nivel suelen ser los mismos que generan fricción en el siguiente. La mayoría de los ejecutivos nunca los identifica — porque son invisibles desde dentro.",
      items: [
        {
          n: "01",
          title: "Fatiga de decisión a escala",
          body: "A medida que crece lo que está en juego, también crece la carga cognitiva de cada decisión. La velocidad y la calidad se degradan — no por falta de información, sino por cómo el cerebro procesa la incertidumbre bajo alta presión.",
        },
        {
          n: "02",
          title: "Presencia de liderazgo bajo presión",
          body: "La capacidad de mantenerse claro, sereno y con autoridad cuando los mercados se mueven, los equipos se fracturan o los acuerdos se estancan — no es personalidad. Es una habilidad entrenable y medible. La mayoría de los líderes no la ha entrenado.",
        },
        {
          n: "03",
          title: "Rendimiento sin recuperación",
          body: "El alto rendimiento crónico sin recuperación cognitiva estructurada no solo causa burnout — degrada la calidad del juicio antes de que el burnout aparezca. Entender el impacto del cuerpo en el rendimiento no es opcional.",
        },
        {
          n: "04",
          title: "Ejecución sin autoría",
          body: "Muchos fundadores y altos ejecutivos son operadores excepcionales. Pocos son originadores — líderes que marcan dirección desde la convicción, no desde la reacción. El paso de uno al otro es el movimiento que define la cima.",
        },
      ],
    },
    differentiator: {
      eyebrow: "El Enfoque",
      title: "Coaching de rendimiento de precisión — no desarrollo personal.",
      lead: "Esto no es un programa de coaching genérico. Es un compromiso estructurado, basado en datos y diseñado individualmente en torno a una sola variable: la brecha entre donde operas hoy y tu verdadero techo.",
      pillars: [
        {
          title: "Línea base biológica primero",
          body: "Cada compromiso comienza con una evaluación de grado médico de cómo tu cuerpo procesa el estrés, regula la energía y se recupera. El coaching construido sobre esta base acelera resultados al trabajar con tu fisiología específica — no con un marco genérico.",
        },
        {
          title: "Identificación de patrones conductuales",
          body: "Los patrones limitantes a nivel ejecutivo rara vez son obvios. Están incrustados en hábitos de comunicación, heurísticas de decisión y reflejos de liderazgo. Identificarlos y rediseñarlos exige un enfoque diagnóstico, no fijar metas genéricas.",
        },
        {
          title: "Resultados de rendimiento medibles",
          body: "Cada elemento del compromiso se mapea a mejoras observables y rastreables — calidad de decisión, impacto de liderazgo, producción cognitiva, alineación de equipo. El progreso no es abstracto.",
        },
      ],
      quotes: [
        {
          q: "Trabajar con Gaia añadió un nivel de claridad y consistencia que no creía posible. La experiencia es verdaderamente transformadora en todas las áreas de mi vida.",
          c: "Matas J. · CEO y Cofundador, ForceField",
        },
        {
          q: "Tras solo los primeros 3 meses trabajando con Gaia, he ganado claridad y estoy en una de las fases más productivas de mi vida profesional.",
          c: "John L. · CEO y Fundador, SpartanBrands",
        },
        {
          q: "Empecé pensando en el negocio, pero hoy se trata de la vida y de todas las dimensiones del liderazgo. El coaching con Gaia ha sido una revolución para mí.",
          c: "Birgir · Emprendedor en Serie",
        },
      ],
    },
    deliverables: {
      eyebrow: "Estructura del Programa",
      titlePre: "12 meses. Totalmente estructurado. ",
      titleEm: "Construido en torno a ti.",
      lead: "Cada entregable se mapea a un resultado de rendimiento medible. Nada en este compromiso existe por apariencia. Todo existe para mover la aguja sobre cómo operas.",
      items: [
        { n: "01", title: "Evaluación de Línea Base de Rendimiento", body: "Perfilado genético y de biomarcadores de grado médico para establecer cómo tu fisiología influye en la cognición, la respuesta al estrés, la gestión de energía y la calidad de decisión. Esto informa cada intervención posterior." },
        { n: "02", title: "Sesiones Semanales 1:1 de Coaching", body: "Sesiones privadas y estructuradas con un único foco: cerrar la brecha entre tu nivel operativo actual y tu techo de capacidad. Las sesiones se adaptan al contexto vivo de tu negocio, no a un currículo fijo." },
        { n: "03", title: "Revisión de Arquitectura de Decisiones", body: "Análisis sistemático de cómo tomas decisiones críticas — dónde la velocidad, el sesgo o la carga cognitiva degradan la calidad. Seguido de un rediseño de tu marco personal de decisión para mayor velocidad y precisión." },
        { n: "04", title: "Desarrollo de Presencia de Liderazgo", body: "Precisión comunicativa, influencia sin fricción y serenidad bajo presión. Medido por cómo te responde tu equipo — no por puntuaciones abstractas de liderazgo." },
        { n: "05", title: "Protocolo de Rendimiento Fisiológico", body: "Nutrición personalizada, arquitectura del sueño y protocolos de recuperación basados en tu perfil genético. La resistencia cognitiva es función de inputs físicos. Esto no es consejo de estilo de vida — es ingeniería del rendimiento." },
        { n: "06", title: "Revisiones Trimestrales de Rendimiento", body: "Revisiones trimestrales estructuradas frente a resultados medibles. Los benchmarks se fijan al inicio y se siguen durante todo el proceso. Sabrás exactamente qué ha cambiado y en qué medida — en cada etapa del compromiso." },
      ],
    },
    socialProof: {
      eyebrow: "Resultados de Clientes",
      titlePre: "Resultados que aparecen donde importa — ",
      titleEm: "en el negocio.",
      quote1: "\u201CNo solo mi negocio ha crecido más del 41% en los últimos 6 meses — también mi forma física, mis relaciones y mi producción mental han mejorado. Ahora tengo total claridad de que todo está interconectado y que tengo plena agencia para construir y rendir al nivel que elija.\u201D",
      attr1: "Michael · Desarrollador Inmobiliario",
      metric1Num: ">60%",
      metric1Label: "Aumento medio de ingresos reportado por clientes en los primeros 6 meses",
      metric2Num: "4 años+",
      metric2Label: "Duración media del compromiso — la mayoría continúa mucho más allá de los 12 meses iniciales",
      quote2: "\u201CGaia me ayudó a identificar patrones conductuales que nunca había nombrado — y no habría podido nombrar sin su enfoque diagnóstico. Como coach yo misma, hace falta mucho para impresionarme y producir resultados que no pueda trabajar de forma independiente. Decir que quedé impresionada es quedarse corto.\u201D",
      attr2: "Jessie B. · Coach Ejecutiva y Fundadora de un Club de Networking",
    },
    credentials: {
      eyebrow: "Trayectoria",
      titleLine1: "Una asesora calibrada para el nivel",
      titleLine2: "en el que realmente operas.",
      lead: "Gaia Ferreira es coach de transformación y estratega conductual con más de 20.000 horas de trabajo uno-a-uno con emprendedores, inversores, ejecutivos y fundadores de alto rendimiento en cuatro continentes. Formada en Psicología y con certificaciones avanzadas en PNL, hipnoterapia y nutrigenómica, se especializa en la intersección entre biología, ciencia conductual y máximo rendimiento, traduciendo ciencia dura en protocolos personalizados que generan cambios medibles y duraderos. Trabaja con equipos enfocados en organizaciones más afiladas, decisivas y sostenibles, y con los líderes que navegan transiciones críticas, mesetas de rendimiento y el coste humano de construir a escala.",
      items: [
        { title: "Tasa de Selección Top 1%", body: "Aceptada en uno de los programas de coaching ejecutivo más competitivos del mundo antes de fundar una práctica independiente premiada al servicio de CEOs, fundadores y profesionales de inversión en cuatro continentes." },
        { title: "24.000+ Horas de Coaching", body: "Impartidas en cuatro idiomas con una base de clientes que abarca CEOs, cofundadores, profesionales de inversión y altos ejecutivos." },
        { title: "Construida en el Mundo Real", body: "Siete años como Ejecutiva de Marketing en Tecnología. Ocho años como emprendedora en Hostelería, incluido su propio programa de TV. Once años haciendo coaching a líderes de alto nivel. El método que utiliza no es teórico; fue probado en industrias, culturas y a lo largo del arco completo de construir algo desde cero." },
        { title: "Metodología Propietaria", body: "Cada compromiso se construye sobre un protocolo personalizado y único para cada cliente, diseñado para identificar patrones de raíz y producir resultados que el coaching estándar no alcanza." },
      ],
      readMore: "Lee la historia completa de Gaia",
    },
    contrast: {
      eyebrow: "Por Qué No las Alternativas",
      titlePre: "No todo el coaching está hecho para ",
      titleEm: "este",
      titlePost: " nivel.",
      lead: "Los programas institucionales, los coaches de fábrica de certificación y los oradores motivacionales tienen su lugar. Ninguno entrega lo que entrega este compromiso.",
      capability: "Capacidad",
      thisEngagement: "Este compromiso",
      rows: [
        "Individualizado a tu biología",
        "Benchmarks de rendimiento medibles",
        "Acceso exclusivo 1:1 a coach sénior",
        "Rediseño de patrones conductuales (no solo metas)",
        "Ingeniería fisiológica del rendimiento",
        "Selección de clientes casi exclusivamente por referencia",
      ],
      cols: [
        "Coaching basado en certificación",
        "Programas corporativos (BetterUp, etc.)",
        "Oradores motivacionales",
      ],
    },
    process: {
      eyebrow: "Cómo Comienza el Compromiso",
      title: "Un proceso tan deliberado como el trabajo mismo.",
      lead: "Gaia trabaja con un número reducido de clientes en cada momento. El compromiso comienza con un proceso estructurado de calificación — porque el ajuste correcto determina si los resultados son siquiera posibles.",
      stepLabel: "Paso",
      steps: [
        { title: "Aplica para una Llamada de Descubrimiento", body: "Responde tres preguntas de calificación para determinar si este compromiso es apropiado para tu contexto y ambiciones actuales." },
        { title: "Conversación de Descubrimiento", body: "Una llamada enfocada de 45 minutos para identificar tus restricciones de rendimiento específicas, tu contexto operativo actual y si hay un encaje claro para el compromiso." },
        { title: "Diseño del Compromiso", body: "Si eres seleccionado, tu programa de 12 meses se diseña específicamente en torno a tu rol, tu negocio y tu perfil de rendimiento — no un currículo estándar." },
        { title: "Compromiso Medible", body: "La semana uno comienza con tu evaluación de línea base biológica. A partir de ahí, cada sesión, protocolo y hito de revisión se mapea a resultados observables." },
      ],
    },
    finalCTA: {
      titleLine1: "Esto no es para todos.",
      titleLine2Pre: "Puede ser exactamente lo correcto para ",
      titleLine2Em: "ti.",
      body: "Si eres fundador o alto ejecutivo operando a un nivel donde las decisiones tienen peso real — y tienes razones para creer que existe una brecha significativa entre donde operas hoy y aquello de lo que realmente eres capaz — esta conversación vale la pena.",
      cta: "Aplica para una Llamada de Descubrimiento",
      disclaimer: "Los compromisos se ofrecen a un número limitado de clientes · Principalmente por referencia · Todas las aplicaciones se revisan personalmente",
    },
    team: {
      eyebrow: "También Disponible",
      title: "¿Necesitas algo para todo el equipo?",
      body: "Hay disponibles formaciones de equipo, intensivos de liderazgo y programas grupales — diseñados para equipos de liderazgo y grupos de alto rendimiento que quieren la misma precisión aplicada a escala. Formato, alcance y entrega se adaptan al tamaño y los objetivos de tu equipo.",
      cta: "Agenda una Llamada de Descubrimiento",
    },
    footer: {
      tagline: "Coaching Ejecutivo y de Liderazgo",
      bottomLeft: (year) => `\u00A9 ${year} Stella Polaris \u00B7 Coaching Ejecutivo y de Liderazgo por Gaia Ferreira`,
      bottomRight: "Solo por aplicación · Práctica internacional",
      apply: "Aplicar",
      appliedLabel: "En revisión",
    },
    applyCTA: {
      applied: "Aplicación en revisión",
      appliedShort: "En revisión",
    },
    apply: {
      step1Eyebrow: "Paso 01 · Tres preguntas de calificación",
      step2Eyebrow: "Paso 02 · Tus datos",
      step1TitlePre: "Aplica para una ",
      step1TitleEm: "Llamada de Descubrimiento",
      step1TitlePost: ".",
      step2Title: "¿Dónde puede contactarte Gaia?",
      step1Sub: "Cada aplicación se revisa personalmente. Estas tres preguntas ayudan a determinar el encaje antes de hablar.",
      step2Sub: "Cada aplicación la lee Gaia. Tendrás respuesta en cinco días hábiles.",
      labelRole: "01 · Tu rol actual",
      labelChallenge: "02 · La restricción más relevante para ti ahora mismo",
      labelReadiness: "03 · Tu disposición para empezar",
      labelName: "Nombre completo",
      labelEmail: "Email",
      labelPhone: "Teléfono (opcional)",
      phonePlaceholder: "+34 600 123 456",
      labelCompany: "Empresa / Organización (opcional)",
      labelReferral: "¿Cómo conociste a Stella Polaris? (opcional)",
      friendDetail: "Su nombre (opcional)",
      otherDetail: "Por favor, especifica",
      labelNotes: "¿Algo que Gaia deba saber antes de la llamada? (opcional)",
      notesPlaceholder: "Contexto, prioridades actuales o lo que haría valiosa esta conversación para ti.",
      cancel: "Cancelar",
      next: "Continuar",
      back: "Atrás",
      submit: "Enviar aplicación",
      submitting: "Enviando…",
      err1: "Por favor, responde las tres preguntas de calificación para continuar.",
      err2: "Por favor, añade tu nombre y un email válido.",
      errGeneric: "Algo salió mal al enviar tu aplicación. Por favor, inténtalo de nuevo.",
      closeLabel: "Cerrar formulario de aplicación",
      success: {
        eyebrow: "Aplicación recibida",
        titlePre: "Tu aplicación ha sido ",
        titleEm: "recibida.",
        body: "Cada aplicación la lee personalmente Gaia. Si hay un encaje claro, recibirás una invitación a una conversación de descubrimiento de 45 minutos en cinco días hábiles.",
        close: "Cerrar",
      },
      roles: [
        { value: "founder_ceo", label: "Fundador / CEO" },
        { value: "c_suite", label: "C-suite (CFO, COO, CTO, CMO…)" },
        { value: "senior_executive", label: "Alto ejecutivo / VP / Director" },
        { value: "investor", label: "Inversor / socio" },
        { value: "other", label: "Otro líder sénior" },
      ],
      challenges: [
        { value: "decision_quality", label: "Calidad de decisión bajo presión" },
        { value: "leadership_presence", label: "Presencia e influencia de liderazgo" },
        { value: "recovery_burnout", label: "Rendimiento, recuperación y gestión de energía" },
        { value: "ownership_vision", label: "Autoría, visión y marcar dirección" },
        { value: "team_performance", label: "Alineación de equipo y rendimiento a escala" },
        { value: "multiple", label: "Varias de las anteriores — me gustaría conversarlo" },
      ],
      readiness: [
        { value: "ready_now", label: "Listo para empezar en 30 días" },
        { value: "next_quarter", label: "En el próximo trimestre" },
        { value: "exploring", label: "Explorando — recabando información" },
      ],
      referrals: [
        { value: "social_media", label: "Redes sociales" },
        { value: "google_ads", label: "Anuncios de Google" },
        { value: "facebook_ads", label: "Anuncios de Facebook" },
        { value: "friend", label: "Un amigo" },
        { value: "other", label: "Otro" },
      ],
    },
    insights: {
      navLabel: "Análisis",
      indexEyebrow: "Análisis",
      indexTitle: "Notas desde el terreno.",
      indexLead: "Notas de trabajo sobre rendimiento ejecutivo, liderazgo bajo presión y los sistemas operativos que están detrás de las decisiones más consecuentes del mundo.",
      readMore: "Leer",
      comingSoon: "Próximamente",
      backToHome: "← Volver al inicio",
      backToIndex: "← Todos los análisis",
      publishedLabel: "Publicado",
    },
    about: {
      eyebrow: "Sobre",
      title: "Una asesora calibrada para el nivel en el que realmente operas.",
      intro: "Gaia Ferreira es coach de transformación y estratega conductual con más de 24.000 horas de trabajo uno-a-uno con emprendedores, inversores, ejecutivos y fundadores de alto rendimiento en cuatro continentes. Formada en Psicología y con certificaciones avanzadas en PNL, hipnoterapia y nutrigenómica, se especializa en la intersección entre biología, ciencia conductual y máximo rendimiento — traduciendo ciencia dura en protocolos personalizados que generan cambios medibles y duraderos.",
      itemsHeading: "Qué hay detrás del trabajo.",
      items: [
        { title: "Tasa de Selección Top 1%", body: "Aceptada en uno de los programas de coaching ejecutivo más competitivos del mundo antes de fundar una práctica independiente premiada al servicio de CEOs, fundadores y profesionales de inversión en cuatro continentes." },
        { title: "24.000+ Horas de Coaching", body: "Impartidas en cuatro idiomas con una base de clientes que abarca CEOs, cofundadores, profesionales de inversión y altos ejecutivos." },
        { title: "Construida en el Mundo Real", body: "Siete años como Ejecutiva de Marketing en Tecnología. Ocho años como emprendedora en Hostelería, incluido su propio programa de TV. Once años haciendo coaching a líderes de alto nivel. El método que utiliza no es teórico — fue probado en industrias, culturas y a lo largo del arco completo de construir algo desde cero." },
        { title: "Metodología Propietaria", body: "Cada compromiso se construye sobre un protocolo personalizado y único para cada cliente, diseñado para identificar patrones de raíz y producir resultados que el coaching estándar no alcanza." },
      ],
      storyEyebrow: "La historia",
      storyTitle: "Un camino largo de allí hasta aquí.",
      storyLead: "Lo que sigue es una mirada más profunda al camino que llevó a Stella Polaris — escrito con las propias palabras de Gaia.",
      storyParas: [
        "No planeaba convertirme en coach.",
        "Pasé 7 años en tecnología como ejecutiva de marketing, y luego 8 años construyendo una serie de negocios propios en hostelería — incluido mi propio programa de TV. Era buena rindiendo. Produciendo. Haciendo que todo se viera pulido por fuera mientras por dentro funcionaba en vacío.",
        "Sabía construir cosas. Lo que no sabía era cómo sostenerme a mí misma mientras lo hacía.",
        "El cambio llegó despacio, y luego de golpe. Empecé a notar que las personas más motivadas a mi alrededor estaban luchando con las mismas heridas invisibles que yo. Tomadores de decisiones brillantes que no podían dormir. Fundadores que habían construido algo real y no sentían nada. Ejecutivos que \u201Cinterpretaban\u201D seguridad en la sala de juntas y se desarmaban en casa. Cuando una amiga mía murió, dejando 3 hijos atrás, igual que los míos — presté atención. La pérdida me golpeó fuerte.",
        "La respuesta convencional a los baches de la vida era terapia, o trabajo de mindset, o una rutina matinal mejor. Probé la mayoría. Incluso trabajé para la empresa de coaching más grande del mundo para entender desde dentro cómo liberar una versión de mí con mejor rendimiento. Ayudaron, parcialmente. Pero trataban los síntomas sin llegar nunca al cableado de debajo.",
        "Así que me fui a buscar lo que de verdad funcionaba. Ya había estudiado psicología formalmente. Añadí PNL. Después hipnoterapia. Después nutrición con especialización en nutrigenómica, porque seguía viendo gente que hacía el trabajo mental y seguía atascada en su cuerpo, su energía, su biología. Necesitaba entender por qué.",
        "Lo que encontré es que el alto rendimiento no se rompe en un solo lugar. Se rompe a la vez en el cuerpo, la mente y la energía: en cómo estás cableada biológicamente, en lo que tu sistema nervioso aprendió a hacer bajo presión, y en las decisiones que tomaste sobre ti misma hace tanto tiempo que ya has dejado de cuestionarlas.",
        "Stella Polaris existe porque yo necesitaba que existiera. Y porque los líderes con los que trabajo necesitaban algo capaz de sostener todo eso, sin simplificarlo en exceso en un marco que sirve para todos y, en realidad, no ayuda a nadie de forma permanente.",
        "Trabajo con un número deliberadamente reducido de personas cada año porque mi trabajo es profundo, con protocolos personalizados y una experiencia de alta intensidad. Por eso los resultados no se revierten cuando termina el compromiso.",
        "Todos estamos haciendo la vida por primera vez. Trabajar juntos mejora su calidad con menos esfuerzo.",
      ],
      ctaTitle: "¿Crees que puedes encajar?",
      ctaSub: "El compromiso comienza con una aplicación estructurada. Cada una se lee personalmente.",
      cta: "Aplica para una Llamada de Descubrimiento",
    },
    contact: {
      eyebrow: "Contacto",
      title: "Dos formas de iniciar una conversación.",
      lead: "Stella Polaris mantiene una base de clientes deliberadamente reducida y Gaia responde personalmente a cada consulta relevante. Elige el camino que se ajusta a lo que buscas.",
      sections: {
        apply: {
          eyebrow: "01 · ¿Quieres trabajar con Gaia?",
          title: "Aplica para una llamada de descubrimiento.",
          body: "Si eres fundador o alto ejecutivo considerando un compromiso de 12 meses, la aplicación es el primer paso correcto. Tres preguntas de calificación y luego tus datos. Se revisa personalmente en cinco días hábiles.",
          cta: "Aplica para una Llamada de Descubrimiento",
        },
        inquiry: {
          eyebrow: "02 · ¿Buscas formador, ponente o invitado para tu podcast?",
          title: "Envía una consulta general.",
          body: "Para medios, alianzas, formaciones de equipo, intensivos de liderazgo o para invitar a Gaia a tu escenario o podcast — usa el formulario a continuación.",
        },
      },
      form: {
        labelName: "Nombre completo",
        labelCompany: "Empresa / Organización",
        labelEmail: "Email",
        labelPhone: "Teléfono",
        phonePlaceholder: "+34 600 123 456",
        labelMessage: "Específicamente quiero conectar contigo porque…",
        messagePlaceholder: "Unas frases sobre contexto, tiempos, audiencia o lo que ayude a Gaia a decidir si participar.",
        submit: "Enviar consulta",
        submitting: "Enviando…",
        cancel: "Borrar",
        err1: "Por favor, añade tu nombre y un email válido.",
        err2: "Por favor, cuéntanos en pocas palabras por qué quieres conectar.",
        errGeneric: "Algo salió mal al enviar tu mensaje. Por favor, inténtalo de nuevo.",
      },
      success: {
        eyebrow: "Consulta recibida",
        titlePre: "Tu mensaje ha sido ",
        titleEm: "recibido.",
        body: "Gracias por escribir. Cada mensaje lo lee personalmente Gaia. Si hay un encaje claro, tendrás respuesta en cinco días hábiles.",
        sendAnother: "Enviar otra",
      },
    },
  },
};
